import React, { useCallback, useState } from 'react'
import { conformToMask } from 'react-text-mask'
import type { GiftCardRecipient } from 'api/api.types'
import { cleanPhone } from 'utils/cleanPhone'
import { validateEmail } from 'utils/validateEmail'
import { focusMaskStart, phoneInputMask } from 'utils/masks'
import { ShadowInput } from 'components/FormElements/ShadowInput/ShadowInput'
import { AppButton } from 'components/FormElements/AppButton/AppButton'
import styles from '../Form.module.scss'

type CertificateRecipientFormProps = {
  data: GiftCardRecipient,
  /** The phone comes back as digits only */
  onSubmit: (recipient: GiftCardRecipient) => void,
}

type Errors = Partial<Record<keyof GiftCardRecipient, boolean>>

/** Who the gift card is sent to: email is required, name and phone are optional */
export const CertificateRecipientForm: React.FC<CertificateRecipientFormProps> = ({ data, onSubmit }) => {
  const [formValues, setFormValues] = useState<GiftCardRecipient>({
    ...data,
    phone: data.phone ? conformToMask('+' + data.phone, phoneInputMask).conformedValue : '',
  })
  const [formErrors, setFormErrors] = useState<Errors>({})

  const setFormField = useCallback((name: keyof GiftCardRecipient, value: string) => {
    setFormValues((values) => ({ ...values, [name]: value }))
  }, [])

  const handleSubmit = useCallback((event: React.FormEvent) => {
    event.preventDefault()
    const phone = cleanPhone(formValues.phone)
    const errors: Errors = {
      email: !formValues.email.length || !validateEmail(formValues.email),
      phone: phone.length > 0 && phone.length < 11,
    }
    setFormErrors(errors)
    if (!errors.email && !errors.phone) {
      onSubmit({ ...formValues, phone })
    }
  }, [formValues, onSubmit])

  const handlePhoneBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (cleanPhone(e.target.value) === '7') {
      setFormField('phone', '')
    }
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.fields}>
        <ShadowInput
          placeholder={'Email *'}
          className={styles.field}
          value={formValues.email}
          error={formErrors.email}
          onChange={(value) => setFormField('email', value)}
        />
        <ShadowInput
          placeholder={'Имя'}
          className={styles.field}
          value={formValues.name}
          error={formErrors.name}
          onChange={(value) => setFormField('name', value)}
        />
        <ShadowInput
          value={formValues.phone}
          error={formErrors.phone}
          type={'tel'}
          mask={phoneInputMask}
          className={styles.field}
          placeholder={'Мобильный телефон'}
          onFocus={focusMaskStart}
          onBlur={handlePhoneBlur}
          onChange={(value) => setFormField('phone', value)}
        />
      </div>
      <div className={styles.actions}>
        <AppButton type={'submit'}>
          Готово
        </AppButton>
      </div>
    </form>
  )
}
